import { Minus, Plus, Stack, StackPlus } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { currencyFormatter } from "lib/utils/formatters";

interface ProductCardProps {
  imageUrl: string;
  title: string;
  subtitle?: string;
  price: number;
  compareAtPrice?: number;
  discountPercentage?: number;
  /** Variant's own currency code — falls back to INR when the variant doesn't carry one */
  currency?: string;
  /** Total variants on the product — the row is hidden when there is only one. */
  variantCount?: number;
  quantity?: number;
  maxQuantity?: number;
  onAddClick?: () => void;
  onRemoveClick?: () => void;
  onClick?: () => void;
  showSubTitle?:boolean;
}

export const ProductCard = ({
  imageUrl,
  title,
  subtitle,
  price,
  compareAtPrice,
  discountPercentage,
  currency = "INR",
  variantCount,
  quantity = 0,
  maxQuantity,
  onAddClick,
  onRemoveClick,
  onClick,
  showSubTitle = true
}: ProductCardProps) =>{
  const isAtMax = maxQuantity != null && quantity >= maxQuantity;
  const format = (amount: number) => currencyFormatter(amount, true, { locale: "en-IN", currency });
  return (

  <a onClick={onClick} className="flex flex-col gap-2 items-start w-full cursor-pointer">
    <div className="h-[120px] overflow-hidden relative rounded-xl shrink-0 w-full shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.1)]">
    {/* Decorative inset border — sits above the image but must never swallow clicks meant for the
        qty controls below it, which would bubble to the card and open the product viewer instead */}
    <div aria-hidden className="absolute inset-0 size-full object-cover rounded-xl z-10 pointer-events-none" style={{ boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.08)" }}></div>

      <img src={imageUrl} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      {Boolean(discountPercentage) && (
        <div className="absolute bg-primary-dark flex flex-col items-start left-0 top-0 px-2 py-1 rounded-br-2xl text-text-xxs-bold text-btnPrimaryText shadow-[0px_0px_0px_1px_rgba(0,0,0,0.1)]">
          <span>{discountPercentage}%</span>
          <span>OFF</span>
        </div>
      )}
      {quantity > 0 ? (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bg-[color-mix(in_srgb,var(--flo-primary-dark-color)_15%,white)] shadow-[0_0_0_1.5px_var(--flo-primary-dark-color)] flex gap-1 items-center overflow-hidden right-[9.5px] top-[79px] rounded-lg h-8">
          <button type="button" onClick={onRemoveClick} className="flex items-center justify-center px-1 h-full">
            <Icon IconComponent={Minus} size="sm" iconProps={{ color: "var(--flo-primary-dark-color)",weight:"bold" }} />
          </button>
          <span className="text-text-sm-semibold text-primary-dark min-w-[10px] text-center">
            {quantity}
          </span>
          <button
            type="button"
            aria-disabled={isAtMax}
            onClick={onAddClick}
            className={`flex items-center justify-center px-1 h-full ${isAtMax ? "opacity-40" : ""}`}>           
             <Icon IconComponent={Plus} size="sm" iconProps={{ color: "var(--flo-primary-dark-color)",weight:"bold" }} />
          </button>
        </div>
      ) : (
        <button
        type="button"
        aria-disabled={isAtMax}
        onClick={(e) => {
          e.stopPropagation();
          onAddClick?.();
        }}
        className={`absolute bg-white shadow-[0_0_0_1.5px_var(--flo-primary-light-color)] flex items-center justify-center p-1.5 right-[9.5px] top-[79px] rounded-lg size-8 ${
          isAtMax ? "opacity-40" : ""
        }`}>       <Icon IconComponent={Plus} size="sm" iconProps={{ weight: "bold", color: "var(--flo-primary-dark-color)" }} />
        </button>
      )}
    </div>
    <div className="flex flex-col items-start justify-center px-1 w-full">
      <p className="text-text-xs-medium text-ink-strong line-clamp-2">
        {title}
      </p>
      {subtitle && showSubTitle  && <p className="text-text-xs-medium text-iron">{subtitle}</p>}
      {Boolean(variantCount && variantCount > 1) && (
        <div className="flex gap-1 items-center">
          <Icon IconComponent={Stack} size="xxxs" iconProps={{ weight: "fill", className: "text-zinc-500 opacity-50" }} />
          <p className="text-text-xs-medium text-iron">{variantCount} Variants</p>
        </div>
      )}
      <div className="flex flex-col items-start justify-center py-0.5">
        <div className="flex items-center text-text-sm-semibold">
         {Boolean(discountPercentage) &&  <span className="text-ink-strong">Get at&nbsp;</span>}
          <span className={Boolean(discountPercentage) ? "text-primary-dark":"text-ink-strong"}>{format(price)} {Boolean(variantCount && variantCount > 1 && !discountPercentage) && <span className="text-text-xs-medium text-ink-strong opacity-50">onwards</span>}</span>
        </div>
        {Boolean(compareAtPrice) && (
          <p className="text-text-xs-medium text-ink-strong opacity-40">
            <span className="line-through">{format(compareAtPrice ?? 0)}</span>
            {Boolean(variantCount && variantCount > 1) && " onwards"}
          </p>
        )}
      </div>
    </div>
  </a>
);}
